import {
  createSharedComposable,
  type UseUrlSearchParamsOptions,
  useUrlSearchParams as useVueUseUrlSearchParams,
} from '@vueuse/core'
import { useRoute } from '#imports'

export const sharedUrlSearchParams = createSharedComposable(
  useVueUseUrlSearchParams,
)

export type UseRouteQueryOptions<T> = Omit<
  UseUrlSearchParamsOptions<T>,
  'write' | 'writeMode'
>

const paramsInstances = new Map<
  string,
  ReturnType<typeof useVueUseUrlSearchParams>
>()

// `createSharedComposable` caches the arg(s) passed to the first call of the composable
// this silently drops arg(s) from new calls which can lead to unexpected behavior
export const useRouteQuery = createSharedComposable(
  <T extends Record<string, any> = Record<string, any>>(
    key?: string,
    options?: UseRouteQueryOptions<T>,
  ) => {
    if (import.meta.server) {
      return useRoute().query as T
    }

    const finalKey = key ?? window.location.pathname
    if (!paramsInstances.has(finalKey)) {
      paramsInstances.set(
        finalKey,
        sharedUrlSearchParams('history', {
          ...options,
          write: true,
          writeMode: 'replace',
        }),
      )
    }
    const query = paramsInstances.get(finalKey)!

    if (window.location.pathname.startsWith('/auth/callback')) {
      ;(query as any).code = undefined
      ;(query as any).state = undefined
    }

    Object.assign(query, getUrlQuery())

    return query
  },
)

function getUrlQuery() {
  if (window.location.pathname.startsWith('/auth')) {
    return {}
  }
  return Object.fromEntries(new URLSearchParams(window.location.search))
}
